// Enhanced Analytics Service for GA4 and Meta Pixel
// This replaces the HTML-based initialization with proper environment variable handling

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
    fbq: any;
    _fbq: any;
  }
}

class AnalyticsService {
  private isProduction: boolean;
  private gaId: string | null;
  private metaPixelId: string | null;
  private gaInitialized = false;
  private metaPixelInitialized = false;

  constructor() {
    this.isProduction = !import.meta.env.DEV && 
                       typeof window !== 'undefined' &&
                       !window.location.hostname.includes('localhost') &&
                       !window.location.hostname.includes('127.0.0.1');

    this.gaId = import.meta.env.VITE_GA_MEASUREMENT_ID || null;
    this.metaPixelId = import.meta.env.VITE_META_PIXEL_ID || null;

    if (this.isProduction) {
      this.initializeGA4();
      this.initializeMetaPixel();
    } else {
      this.setupDevelopmentMode();
    }
  }

  private setupDevelopmentMode() {
    console.log('Analytics running in development mode');
    // Create mock functions for development
    window.dataLayer = window.dataLayer || [];
    window.gtag = (...args: any[]) => {
      console.log('GA4 (dev):', args);
    };
    window.fbq = (...args: any[]) => {
      console.log('Meta Pixel (dev):', args);
    };
  }

  private initializeGA4() {
    if (!this.gaId) {
      console.warn('Google Analytics ID not found in environment variables');
      return;
    }

    if (!this.gaId.startsWith('G-')) {
      console.warn('Google Analytics ID format invalid:', this.gaId);
      return;
    }

    try {
      // Load gtag script dynamically
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${this.gaId}`;
      document.head.appendChild(script);

      // Initialize dataLayer and gtag
      window.dataLayer = window.dataLayer || [];
      window.gtag = function() {
        window.dataLayer.push(arguments);
      };

      window.gtag('js', new Date());
      window.gtag('config', this.gaId, {
        page_title: 'MockTrail Landing Page',
        page_location: window.location.href,
        send_page_view: true,
        anonymize_ip: true,
        allow_google_signals: true,
        allow_ad_personalization_signals: true
      });

      this.gaInitialized = true;
      console.log('Google Analytics initialized successfully with ID:', this.gaId);
    } catch (error) {
      console.error('Google Analytics initialization error:', error);
    }
  }

  private initializeMetaPixel() {
    if (!this.metaPixelId) {
      console.warn('Meta Pixel ID not found in environment variables');
      return;
    }

    if (this.metaPixelId.length < 10) {
      console.warn('Meta Pixel ID format invalid:', this.metaPixelId);
      return;
    }

    try {
      // Meta Pixel Base Code
      const f = window as any;
      const b = document;
      const e = 'script';
      const v = 'https://connect.facebook.net/en_US/fbevents.js';
      
      if (f.fbq) return;
      
      // Create fbq function with proper typing
      const n = f.fbq = function() {
        (n as any).callMethod ? (n as any).callMethod.apply(n, arguments) : (n as any).queue.push(arguments);
      };
      
      if (!f._fbq) f._fbq = n;
      (n as any).push = n;
      (n as any).loaded = true;
      (n as any).version = '2.0';
      (n as any).queue = [];
      
      const t = b.createElement(e);
      t.async = true;
      t.src = v;
      
      const s = b.getElementsByTagName(e)[0];
      s.parentNode?.insertBefore(t, s);

      // Initialize with Pixel ID
      window.fbq('init', this.metaPixelId);
      window.fbq('track', 'PageView');

      this.metaPixelInitialized = true;
      console.log('Meta Pixel initialized successfully with ID:', this.metaPixelId.substring(0, 6) + '...');
    } catch (error) {
      console.error('Meta Pixel initialization error:', error);
    }
  }

  // GA4 Event Tracking
  trackEvent(eventName: string, parameters?: Record<string, any>) {
    if (this.isProduction && this.gaInitialized && window.gtag) {
      window.gtag('event', eventName, parameters);
    } else if (!this.isProduction && window.gtag) {
      window.gtag('event', eventName, parameters);
    }
  }

  // GA4 Page View Tracking
  trackPageView(pageTitle?: string, pagePath?: string) {
    if (this.isProduction && this.gaInitialized && window.gtag) {
      window.gtag('config', this.gaId!, {
        page_title: pageTitle || document.title,
        page_location: pagePath || window.location.href,
      });
    }
  }

  // Meta Pixel Event Tracking
  trackMetaPixelEvent(eventName: string, parameters?: Record<string, any>) {
    if (this.isProduction && this.metaPixelInitialized && window.fbq) {
      if (this.isStandardMetaEvent(eventName)) {
        window.fbq('track', eventName, parameters);
      } else {
        window.fbq('trackCustom', eventName, parameters);
      }
    } else if (!this.isProduction && window.fbq) {
      window.fbq('trackCustom', eventName, parameters);
    }
  }

  // Check if event is a standard Meta Pixel event
  private isStandardMetaEvent(eventName: string): boolean {
    const standardEvents = [
      'AddPaymentInfo', 'AddToCart', 'AddToWishlist', 'CompleteRegistration',
      'Contact', 'CustomizeProduct', 'Donate', 'FindLocation', 'InitiateCheckout',
      'Lead', 'PageView', 'Purchase', 'Schedule', 'Search', 'StartTrial',
      'SubmitApplication', 'Subscribe', 'ViewContent'
    ];
    return standardEvents.includes(eventName);
  }

  // Combined tracking for both platforms
  trackUserAction(actionName: string, parameters?: Record<string, any>) {
    // Track in GA4
    this.trackEvent('user_action', {
      action_name: actionName,
      ...parameters,
      timestamp: new Date().toISOString()
    });

    // Track in Meta Pixel as custom event
    this.trackMetaPixelEvent('UserAction', {
      action_name: actionName,
      ...parameters
    });
  }

  // Track conversions
  trackConversion(conversionType: string, value?: number, currency?: string) {
    // GA4 conversion
    this.trackEvent('conversion', {
      conversion_type: conversionType,
      value: value,
      currency: currency || 'USD'
    });

    // Meta Pixel conversion
    if (conversionType === 'signup') {
      this.trackMetaPixelEvent('CompleteRegistration', { value, currency });
    } else if (conversionType === 'purchase') {
      this.trackMetaPixelEvent('Purchase', { value, currency });
    } else {
      this.trackMetaPixelEvent('Lead', { value, currency });
    }
  }

  // Get initialization status
  getStatus() {
    return {
      isProduction: this.isProduction,
      gaInitialized: this.gaInitialized,
      metaPixelInitialized: this.metaPixelInitialized,
      gaId: this.gaId ? this.gaId.substring(0, 6) + '...' : null,
      metaPixelId: this.metaPixelId ? this.metaPixelId.substring(0, 6) + '...' : null
    };
  }
}

// Create singleton instance
const analyticsService = new AnalyticsService();

// Export the service and convenience functions
export default analyticsService;

export const trackEvent = (eventName: string, parameters?: Record<string, any>) => {
  analyticsService.trackEvent(eventName, parameters);
};

export const trackPageView = (pageTitle?: string, pagePath?: string) => {
  analyticsService.trackPageView(pageTitle, pagePath);
};

export const trackMetaPixelEvent = (eventName: string, parameters?: Record<string, any>) => {
  analyticsService.trackMetaPixelEvent(eventName, parameters);
};

export const trackUserAction = (actionName: string, parameters?: Record<string, any>) => {
  analyticsService.trackUserAction(actionName, parameters);
};

export const trackConversion = (conversionType: string, value?: number, currency?: string) => {
  analyticsService.trackConversion(conversionType, value, currency);
};

export const getAnalyticsStatus = () => {
  return analyticsService.getStatus();
};

// Backward compatibility exports for existing functions
export const trackButtonClick = (buttonName: string, location: string, additionalParams?: Record<string, any>) => {
  // Track in GA4
  analyticsService.trackEvent('button_click', {
    button_name: buttonName,
    button_location: location,
    event_category: 'engagement',
    event_label: `${buttonName} - ${location}`,
    ...additionalParams
  });

  // Track in Meta Pixel with proper event classification
  const lowerButtonName = buttonName.toLowerCase();
  let metaEvent = null;
  let useStandardEvent = false;

  if (lowerButtonName.includes('sign up') || lowerButtonName.includes('try') || lowerButtonName.includes('register')) {
    metaEvent = 'Lead';
    useStandardEvent = true;
  } else if (lowerButtonName.includes('pricing') || lowerButtonName.includes('view')) {
    metaEvent = 'ViewContent';
    useStandardEvent = true;
  } else if (lowerButtonName.includes('demo') || lowerButtonName.includes('start') || lowerButtonName.includes('begin')) {
    metaEvent = 'InitiateCheckout';
    useStandardEvent = true;
  } else if (lowerButtonName.includes('contact') || lowerButtonName.includes('support')) {
    metaEvent = 'Contact';
    useStandardEvent = true;
  }

  if (useStandardEvent && metaEvent) {
    analyticsService.trackMetaPixelEvent(metaEvent, {
      button_name: buttonName,
      button_location: location,
      content_name: buttonName,
      content_category: 'button_click',
      ...additionalParams
    });
  } else {
    // Use custom event for generic button clicks
    analyticsService.trackMetaPixelEvent('ButtonClick', {
      button_name: buttonName,
      button_location: location,
      event_type: 'generic_button_click',
      ...additionalParams
    });
  }
};

export const trackCustomEvent = (eventName: string, eventData?: Record<string, any>) => {
  analyticsService.trackEvent(eventName, eventData);
};

export const trackSectionView = (sectionName: string, additionalParams?: Record<string, any>) => {
  analyticsService.trackEvent('section_view', {
    section_name: sectionName,
    event_category: 'engagement',
    event_label: sectionName,
    ...additionalParams
  });
};

export const trackFormInteraction = (action: 'start' | 'submit' | 'error', formName: string, additionalParams?: Record<string, any>) => {
  const eventName = action === 'submit' ? 'form_submit' : 
                    action === 'start' ? 'form_start' : 'form_error';
  
  analyticsService.trackEvent(eventName, {
    form_name: formName,
    form_action: action,
    event_category: 'form',
    event_label: formName,
    ...additionalParams
  });
};

export const trackVideoInteraction = (action: 'play' | 'pause' | 'complete' | 'seek', videoName: string, additionalParams?: Record<string, any>) => {
  analyticsService.trackEvent(`video_${action}`, {
    video_name: videoName,
    video_action: action,
    event_category: 'video',
    event_label: videoName,
    ...additionalParams
  });
}; 